import React, { createContext, ReactNode, useState, useEffect } from 'react'

import { CPF } from 'gerador-validador-cpf'

import PlanGroupsService, { PlanGroup } from 'services/planGroups'
import PatientsService from 'services/patients'
import { isValid, parse } from 'date-fns'
import { useQuery } from '@tanstack/react-query'
import { planGroupsSettings } from 'settings/_planGroupSettings'

type SetStateType = (value: string) => void

type Gender = {
  id: number
  label: 'FEMININO' | 'MASCULINO' | 'OUTRO'
  code: 'FEM' | 'MAS' | 'NDA'
}

interface SignupContextData {
  firstName: string | null
  lastName: string | null
  cpf: string | null
  dateBirth: string | null
  gender: Gender | null
  email: string | null
  password: string | null
  cellPhone: string
  planGroup: PlanGroup
  isFirstNameValid: boolean
  isLastNameValid: boolean
  isCpfValid: boolean
  isDateBirthValid: boolean
  isGenderValid: boolean
  isEmailValid: boolean
  isPasswordValid: boolean
  firstNameMsg: string
  lastNameMsg: string
  cpfMsg: string
  dateBirthMsg: string
  genderMsg: string
  emailMsg: string
  passwordMsg: string
  hasEnterpriseMedicalRequest: 'yes' | 'no' | null
  daysToAuthorizeProcedure: number
  setFirstName: SetStateType
  setLastName: SetStateType
  setCpf: SetStateType
  setDateBirth: SetStateType
  setGender: (value: Gender) => void
  setEmail: SetStateType
  setPassword: SetStateType
  setCellPhone: SetStateType
  setIsFirstNameValid: (value: boolean) => void
  setIsLastNameValid: (value: boolean) => void
  setIsCpfValid: (value: boolean) => void
  setIsDateBirthValid: (value: boolean) => void
  setIsGenderValid: (value: boolean) => void
  setIsPasswordValid: (value: boolean) => void
  setPlanGroup: (value: PlanGroup) => void
  setHasEnterpriseMedicalRequest: (value: 'yes' | 'no' | null) => void
  listPlanGroups: () => Promise<PlanGroup[]>
}

interface SignupProviderProps {
  children: ReactNode
}

export const SignupContext = createContext<SignupContextData>(
  {} as SignupContextData
)

export function SignupProvider({ children }: SignupProviderProps): ReactNode {

  const [firstName, setFirstName] = useState<string | null>(null)
  const [lastName, setLastName] = useState<string | null>(null)
  const [cpf, setCpf] = useState<string | null>(null)
  const [dateBirth, setDateBirth] = useState<string | null>(null)
  const [gender, setGender] = useState<Gender | null>(null)
  const [email, setEmail] = useState<string | null>(null)
  const [password, setPassword] = useState<string | null>(null)
  const [cellPhone, setCellPhone] = useState('')
  const [isFirstNameValid, setIsFirstNameValid] = useState<boolean>(false)
  const [isLastNameValid, setIsLastNameValid] = useState<boolean>(false)
  const [isCpfValid, setIsCpfValid] = useState<boolean>(false)
  const [isDateBirthValid, setIsDateBirthValid] = useState<boolean>(false)
  const [isGenderValid, setIsGenderValid] = useState<boolean>(false)
  const [isPasswordValid, setIsPasswordValid] = useState<boolean>(false)
  const [firstNameMsg, setFirstNameMsg] = useState('')
  const [lastNameMsg, setLastNameMsg] = useState('')
  const [cpfMsg, setCpfMsg] = useState('')
  const [dateBirthMsg, setDateBirthMsg] = useState('')
  const [genderMsg, setGenderMsg] = useState('')
  const [emailMsg, setEmailMsg] = useState('')
  const [passwordMsg, setPasswordMsg] = useState('')
  const [planGroup, setPlanGroup] = useState<PlanGroup>({
    code: null,
    name: '',
    category: null,
    use_atena: undefined
  })
  const [hasEnterpriseMedicalRequest, setHasEnterpriseMedicalRequest] = useState<'yes' | 'no' | null>(null)

  const daysToAuthorizeProcedure =
    planGroupsSettings[planGroup?.code]?.daysToAuthorizeProcedure || 3

  const mailProps = { pattern: new RegExp(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i) }
  const isEmailValid = mailProps.pattern.test(email)

  const { isError: isEmailAvailable, isLoading } = useQuery({ 
    queryKey: ['isEmailAvailable', email], 
    queryFn: () => PatientsService.isEmailAvailable(email),
    enabled: isEmailValid,
    staleTime: 0
  })

  useEffect(() => {
    if (cpf === null) {
      setIsCpfValid(false)  
      setCpfMsg('')
      return
    }

    const isValid = CPF.validate(cpf) //eslint-disable-line
    const formattedCpf = CPF.format(cpf, 'digits') //eslint-disable-line
    
    if (cpf) {
      if(isValid) {
        PatientsService.isCpfAvailable(formattedCpf)
          .then(() => {
            setIsCpfValid(false)   
            setCpfMsg('CPF já cadastrado')
          })
          .catch(() => {
            setIsCpfValid(true)
            setCpfMsg('')
          })

      } else {
        setIsCpfValid(false)
        setCpfMsg('CPF inválido')
      }
    } else {
      setIsCpfValid(false)
      setCpfMsg('O Campo é obrigatório')
    }
  }, [cpf])

  useEffect(() => {
    if (dateBirth === null) {
      setIsDateBirthValid(false)
      setDateBirthMsg('')
      return
    }

    const parsedDate = parse(dateBirth, 'dd/MM/yyyy', new Date())
    const isValidDate = isValid(parsedDate)

    if (dateBirth) {
      if (isValidDate) {
        setIsDateBirthValid(true)
        setDateBirthMsg('')
      } else {
        setIsDateBirthValid(false)
        setDateBirthMsg('Data de nascimento inválida')
      }
     
    } else {
      setIsDateBirthValid(false)
      setDateBirthMsg('O Campo é obrigatório')
    }
  }, [dateBirth])

  useEffect(() => {
    if (gender === null) {
      setIsGenderValid(false)
      setGenderMsg('')
      return
    }

    if (gender) {
      setIsGenderValid(true)
      setGenderMsg('')
    } else {
      setIsGenderValid(false)
      setGenderMsg('O Campo é obrigatório')
    }
  }, [gender])

  useEffect(() => {
    if (email === null) {
      setEmailMsg('')
    } else if (email === '') {
      setEmailMsg('O Campo é obrigatório')
    } else if (!isEmailValid) {
      setEmailMsg('Email inválido')
    } else if (!isEmailAvailable && !isLoading) {
      setEmailMsg('Ops! Já existe uma conta com este e-mail, <a href="/entrar">faça seu login.</a>')
    } else {
      setEmailMsg('')
    }
  }, [email, isEmailAvailable, isEmailValid, isLoading])

  useEffect(() => {
    if (firstName === null) {
      setIsFirstNameValid(false)  
      setFirstNameMsg('')
      return
    }

    const nameProps = { pattern: new RegExp(/^[A-Za-zÀ-ÿ ]+$/) }
    const isValid = nameProps.pattern.test(firstName)

    if (firstName) {
      if(isValid) {
        setIsFirstNameValid(true)
        setFirstNameMsg('')
      } else if(firstName) {
        setIsFirstNameValid(false)
        setFirstNameMsg('Nome inválido')
      }
    } else {
      setIsFirstNameValid(false)
      setFirstNameMsg('O Campo é obrigatório')
    }
  }, [firstName])

  useEffect(() => {
    if (lastName === null) {
      setIsLastNameValid(false)  
      setLastNameMsg('')
      return
    }

    const nameProps = { pattern: new RegExp(/^[A-Za-zÀ-ÿ ]+$/) }
    const isValid = nameProps.pattern.test(lastName)

    if (lastName) {
      if(isValid) {
        setIsLastNameValid(true)
        setLastNameMsg('')
      } else if(lastName) {
        setIsLastNameValid(false)
        setLastNameMsg('Sobrenome inválido')
      }
    } else {
      setIsLastNameValid(false)
      setLastNameMsg('O Campo é obrigatório')
    }
  }, [lastName])

  useEffect(() => {
    if (password === null) {
      setIsPasswordValid(false)  
      setPasswordMsg('')
      return
    }

    const passwordProps = {
      minLength: 6,
      maxLength: 20,
    }

    const shortPassword = password.length < passwordProps.minLength
    const longPassword = password.length > passwordProps.maxLength

    if (password) {
      if(shortPassword) {
        setIsPasswordValid(false)
        setPasswordMsg('A senha é muito curta')
      } else if(longPassword) {
        setIsPasswordValid(false)
        setPasswordMsg('A senha é muito longa')
      } else {
        setIsPasswordValid(true)
        setPasswordMsg('')
      }
    } else {
      setIsPasswordValid(false)
      setPasswordMsg('O Campo é obrigatório')
    }
  }, [password])

  return (
    <SignupContext.Provider 
      value={{
        firstName,
        lastName,
        cpf,
        dateBirth,
        gender,
        email,
        password,
        cellPhone,
        planGroup,
        isFirstNameValid,
        isLastNameValid,
        isCpfValid,
        isDateBirthValid,
        isGenderValid,
        isEmailValid: isEmailValid && isEmailAvailable,
        isPasswordValid,
        firstNameMsg,
        lastNameMsg,
        cpfMsg,
        dateBirthMsg,
        genderMsg,
        emailMsg,
        passwordMsg,
        hasEnterpriseMedicalRequest,
        daysToAuthorizeProcedure,
        setFirstName,
        setLastName,
        setCpf,
        setDateBirth,
        setGender,
        setEmail,
        setPassword,
        setCellPhone,
        setIsFirstNameValid,
        setIsLastNameValid,
        setIsCpfValid,
        setIsDateBirthValid,
        setIsGenderValid,
        setIsPasswordValid,
        setPlanGroup,
        setHasEnterpriseMedicalRequest,
        listPlanGroups: PlanGroupsService.listPlanGroups,
      }}
    >
      {children}
    </SignupContext.Provider>
  )
}